import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../config/api";

type UserRole = "POWER_ADMIN" | "ADMIN" | "MANAGER" | "STAFF" | "VIEWER";

type CreateUserResponse = {
  ok?: boolean;
  id?: number;
  user?: {
    id: number;
    username: string;
    full_name: string;
    role: UserRole;
    is_active: boolean;
  };
  error?: string;
};

type CreateUserForm = {
  username: string;
  full_name: string;
  password: string;
  confirm_password: string;
  role: UserRole;
  is_active: boolean;
};

const CREATE_USER_API = apiUrl("/users");
const TOKEN_STORAGE_KEY = "bg_shop_token";

const ROLE_OPTIONS: Array<{ value: UserRole; label: string }> = [
  { value: "POWER_ADMIN", label: "POWER ADMIN" },
  { value: "ADMIN", label: "ADMIN" },
  { value: "MANAGER", label: "MANAGER" },
  { value: "STAFF", label: "STAFF" },
  { value: "VIEWER", label: "VIEWER" },
];

const INITIAL_FORM: CreateUserForm = {
  username: "",
  full_name: "",
  password: "",
  confirm_password: "",
  role: "STAFF",
  is_active: true,
};

function getErrorMessage(code: string): string {
  switch (code) {
    case "username_required":
      return "Please enter a username.";
    case "full_name_required":
      return "Please enter a full name.";
    case "password_required":
      return "Please enter a password.";
    case "password_too_short":
      return "Password must be at least 6 characters.";
    case "invalid_role":
      return "Selected role is invalid.";
    case "username_duplicate":
      return "This username already exists.";
    case "forbidden":
      return "This account does not have permission to create users.";
    case "missing_auth_token":
    case "invalid_auth_token":
      return "Please log in again before creating a user.";
    default:
      return "Unable to create the user right now.";
  }
}

export default function CreateUserPage(): JSX.Element {
  const [form, setForm] = useState<CreateUserForm>(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const passwordMismatch = useMemo(() => {
    return form.confirm_password.length > 0 && form.password !== form.confirm_password;
  }, [form.confirm_password, form.password]);

  // PMN-CRITICAL(/CreateUserPage): This creates a users row only; sale channel access must be assigned later in UserListPage.
  async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!form.username.trim()) {
      setError("Please enter a username.");
      return;
    }
    if (!form.full_name.trim()) {
      setError("Please enter a full name.");
      return;
    }
    if (!form.password) {
      setError("Please enter a password.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (passwordMismatch) {
      setError("Password confirmation does not match.");
      return;
    }

    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? "";
    if (!token) {
      setError("Please log in again before creating a user.");
      return;
    }

    setSaving(true);
    try {
      const resp = await fetch(CREATE_USER_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: form.username.trim(),
          full_name: form.full_name.trim(),
          password: form.password,
          role: form.role,
          is_active: form.is_active,
        }),
      });

      const data = (await resp.json().catch(() => ({}))) as CreateUserResponse;
      if (!resp.ok || !data.ok || !data.user) {
        throw new Error(data.error ?? "failed_to_create_user");
      }

      setSuccessMessage(`User ${data.user.username} was created successfully.`);
      setForm({
        ...INITIAL_FORM,
        role: form.role,
        is_active: form.is_active,
      });
    } catch (err) {
      const code = err instanceof Error ? err.message : "failed_to_create_user";
      setError(getErrorMessage(code));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="min-vh-100 bg-primary py-4 py-md-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8 col-xl-7">
            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-4 p-md-5">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                  <div>
                    <h2 className="text-primary mb-1">Create User</h2>
                    <div className="text-muted">
                      Add a new account and assign the access level for this user.
                    </div>
                  </div>
                  <Link to="/" className="btn btn-outline-primary">
                    Back to Menu
                  </Link>
                </div>

                {error && <div className="alert alert-danger rounded-3">{error}</div>}
                {successMessage && (
                  <div className="alert alert-success rounded-3">{successMessage}</div>
                )}

                <form className="d-grid gap-3" onSubmit={(e) => void handleSubmit(e)}>
                  <div className="row g-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.username}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, username: e.target.value }))
                        }
                        autoComplete="username"
                        placeholder="Enter username"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Full Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.full_name}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, full_name: e.target.value }))
                        }
                        placeholder="Enter full name"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-control"
                        value={form.password}
                        onChange={(e) =>
                          setForm((prev) => ({ ...prev, password: e.target.value }))
                        }
                        autoComplete="new-password"
                        placeholder="At least 6 characters"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Confirm Password</label>
                      <input
                        type="password"
                        className={`form-control ${passwordMismatch ? "is-invalid" : ""}`}
                        value={form.confirm_password}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            confirm_password: e.target.value,
                          }))
                        }
                        autoComplete="new-password"
                        placeholder="Re-enter password"
                      />
                      {passwordMismatch && (
                        <div className="invalid-feedback">Password confirmation does not match.</div>
                      )}
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label">Role</label>
                      <select
                        className="form-select"
                        value={form.role}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            role: e.target.value as UserRole,
                          }))
                        }
                      >
                        {ROLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-12 col-md-6 d-flex align-items-end">
                      <div className="form-check form-switch mb-2">
                        <input
                          id="create-user-active"
                          type="checkbox"
                          className="form-check-input"
                          checked={form.is_active}
                          onChange={(e) =>
                            setForm((prev) => ({ ...prev, is_active: e.target.checked }))
                          }
                        />
                        <label htmlFor="create-user-active" className="form-check-label">
                          Active account
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column flex-sm-row gap-2 justify-content-end mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setError("");
                        setSuccessMessage("");
                        setForm(INITIAL_FORM);
                      }}
                      disabled={saving}
                    >
                      Reset
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Creating User..." : "Create User"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
